import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Loader2 } from "lucide-react";
import { API } from "@/lib/brand";
import { categoryLabel, formatPublishedDate, resolveImageUrl } from "@/lib/journal";
import JournalMasthead from "@/components/journal/JournalMasthead";
import ShareRow from "@/components/journal/ShareRow";
import RelatedPosts from "@/components/journal/RelatedPosts";
import PresetHero from "@/components/journal/PresetHero";
import NewsletterBox from "@/components/journal/NewsletterBox";

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState("loading");
      try {
        const res = await fetch(`${API}/journal/posts/${slug}`);
        if (!res.ok) {
          if (!cancelled) setState("notfound");
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setPost(data);
          setState("ready");
        }
      } catch {
        if (!cancelled) setState("notfound");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-ink-soft">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  if (state === "notfound" || !post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="eyebrow mb-3">404</div>
        <h1 className="font-display text-3xl text-ink mb-4">Post not found</h1>
        <Link to="/journal" className="link-brass inline-flex items-center gap-1.5">
          <ArrowLeft size={14} /> Back to the Journal
        </Link>
      </div>
    );
  }

  const url = typeof window !== "undefined" ? window.location.href : "";
  const img = resolveImageUrl(post.featured_image_url);

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-16">
      <Helmet>
        <title>{`${post.title} — The Reliserve Wealth Journal`}</title>
        <meta name="description" content={post.excerpt || post.title} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        {img && <meta property="og:image" content={img} />}
        <meta property="og:url" content={url} />
        <meta name="twitter:card" content={img ? "summary_large_image" : "summary"} />
      </Helmet>

      <JournalMasthead compact />

      <div className="mb-8">
        <Link to="/journal" className="link-brass inline-flex items-center gap-1.5 text-[13px]">
          <ArrowLeft size={14} /> Back to all posts
        </Link>
      </div>

      <article data-testid="journal-post">
        <div className="flex items-center gap-3 mb-4">
          <span className="chip">{categoryLabel(post.category)}</span>
          <span className="num text-ink-soft text-[12px]">
            {formatPublishedDate(post.published_at)}
          </span>
        </div>

        <h1
          className="font-display text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight"
          data-testid="post-title"
        >
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="mt-5 text-[17px] md:text-[18px] text-ink-soft leading-relaxed">
            {post.excerpt}
          </p>
        )}

        {img ? (
          <div className="mt-8 aspect-[16/9] overflow-hidden rounded-md border hairline">
            <img src={img} alt={post.title} className="w-full h-full object-cover" />
          </div>
        ) : post.featured_preset ? (
          <div className="mt-8 aspect-[16/9] overflow-hidden rounded-md border hairline">
            <PresetHero presetId={post.featured_preset} title={post.title} />
          </div>
        ) : null}

        <ShareRow url={url} title={post.title} />

        <div
          className="journal-body"
          data-testid="post-body"
          dangerouslySetInnerHTML={{ __html: post.body || "" }}
        />

        <div className="mt-12 pt-6 border-t hairline">
          <ShareRow url={url} title={post.title} />
        </div>

        <NewsletterBox source={post.slug} />
      </article>

      <RelatedPosts slug={post.slug} limit={3} />
    </div>
  );
}
